import { Divider } from 'antd';
import React from 'react';
import { useDispatch } from 'react-redux';
import { openPopup } from '../../../../components/Popup/_components/WrapperPopup';
import { callEventBiology, getBiology, useBiology } from '../../../../contexts/PlanOperationContext/Biology';
import { datetime } from '../../../../util/library/datetime';
import { KEY_STORAGE, OPERATION_TYPE } from '../../../../util/varibles/constants';
import { IPropsChildren, IService, IServiceRequest, IVessel } from '../../../../util/varibles/interface';
import { notify, NotifyCode } from '../../../../util/varibles/message';
import { initTask } from '../../Popup/EditOp/Service/constants';
import { planOpActions } from '../../reducer';
import Location from "../_components/Location";
import { deleteDragEl, KEY_SPLIT, MODE_DRAG } from '../constants';
import styles from './style.module.scss';

const onChangePosition = (e: any) => {
    const position = [e.screenX, e.screenY].join(KEY_SPLIT);
    localStorage.setItem(KEY_STORAGE.POSITION, position);
}

const createDragEl = (data: IProps['data']) => {
    const el = openPopup(<div className={styles.dragView}>
        <div className="flex items-center gap-[2px] leadding-[18px] flex-wrap">
            {data.id}
            <Divider orientation='vertical' />
            <Location source={[data.name]} style={{ fontSize: '15px', fontWeight: 500, lineHeight: '18px' }} />
        </div>
    </div>);
    el.style.position = 'absolute';
    el.style.zIndex = '9999';
    el.style.display = 'none';
    return el;
}

const areEqual = (prevProps: IProps, nextProps: IProps) => {
    const isChange = prevProps.isError !== nextProps.isError
        || prevProps.message !== nextProps.message
        || prevProps.messageText !== nextProps.messageText
        || JSON.stringify(prevProps.style) !== JSON.stringify(nextProps.style)
        || JSON.stringify(prevProps.data) !== JSON.stringify(nextProps.data)

    return !isChange
};

export const isPast = (data: IServiceRequest) => {
    const { operations = [], vessel_resources = [], start_time } = data;
    return operations.length === 0 && vessel_resources.length === 0 && start_time && datetime(start_time).time < datetime().startOf('day').time;
}

interface IProps extends IPropsChildren {
    data: IServiceRequest
    isError?: boolean
    message?: any
    messageText?: any
    style?: any

    onContext?(e: React.MouseEvent<HTMLDivElement>): void
}

const Card: React.FC<IProps> = React.memo(props => {
    const {
        data,
        isError = false,
        message,
        messageText,
        style,
        children,
        onContext
    } = props;
    const dispatch = useDispatch();
    const [isAllowedToCreate, set] = useBiology(state => state.isAllowedToCreate);
    const get = getBiology();
    const { updateDragEl } = callEventBiology();

    function handleMouseDown(e: any) {
        if (e.button !== 0)
            return;

        if (isError)
            return;

        document.addEventListener('mouseup', handleMouseUp);
        document.body.setAttribute('data-is-prevent-select', 'true');

    }

    const handleMouseUp = () => {
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.setAttribute('data-is-prevent-select', 'false');
    };

    const handleContextMenu = (e: any) => {
        e.preventDefault();
        if (onContext)
            onContext(e);
    }

    const handleDragStart = () => {
        const el = createDragEl(data);
        localStorage.setItem(KEY_STORAGE.REQUIRE_OPERATION_TYPE, JSON.stringify([OPERATION_TYPE.SERVICE]));
        localStorage.setItem(KEY_STORAGE.UNITS_SELECTED, JSON.stringify({}));
        set({ dragEl: el });

        document.body.style.overflow = 'hidden';
        document.body.addEventListener('dragover', handleDragOver);
        document.body.addEventListener('drop', handleDrop);
    }

    const handleDragOver = (e: MouseEvent) => {
        const { pageX, pageY } = e;
        updateDragEl(pageX - 10, pageY - 20);
        onChangePosition(e);
        e.preventDefault();
    }

    const handleDrop = (e: MouseEvent) => {
        const { dragEl } = get()
        deleteDragEl(dragEl);
        const location = { id: data.site_id, name: data.name };
        const { start_time = 0, finish_time = 0 } = data;
        const duration = finish_time - start_time;
        const operation: Partial<IService> = {
            request_tab_id: data.id,
            tasks: [initTask({ location_id: location.id || "", location_name: location.name || "", note: data.description, })]
        }
        if (operation.tasks)
            operation.tasks[0].duration = duration;
        dispatch(planOpActions.togglePopupOperation({
            e,
            location,
            operation,
            useDroppedStartTime: true,
            checkMiddle: ({ vessel }: { vessel: IVessel }) => {
                const { operations = [] } = data;
                const ids = new Set(operations.map(item => item.vessel_id));
                if (ids.size === 0)
                    return true;
                if (ids.has(vessel.id)) {
                    notify.error(NotifyCode.E52)();
                    return false
                }
                return true;
            }
        }))
    }

    const handleDragEnd = (e: React.DragEvent) => {
        const target = e.currentTarget as HTMLDivElement;
        target.style.cursor = 'grab';
        const mode = MODE_DRAG.END;
        localStorage.setItem(KEY_STORAGE.MODE_DRAG, mode);
        document.body.style.overflow = '';
        document.body.removeEventListener('dragover', handleDragOver);
        document.body.removeEventListener('drop', handleDrop);
        const { dragEl } = get()
        deleteDragEl(dragEl);
    }

    if (!isAllowedToCreate) {
        return <div
            className={styles.card}
            style={style}
            onContextMenu={handleContextMenu}
        >
            {children}
            <div className={styles['drag-view']} style={{ cursor: "default" }}>
                {message && <div className={styles.note} title={messageText ? messageText : message}>{message}</div>}
            </div>
        </div>
    }

    return <div
        className={styles.card}
        onMouseDown={handleMouseDown}
        onContextMenu={handleContextMenu}
        data-old={isPast(data)}
        style={style}
    >
        {children}
        <div
            className={styles['drag-view']}
            draggable={!isError}
            onDragStart={(e) => !isError ? handleDragStart() : null}
            onDragEnd={handleDragEnd}
        >
            {message && <div className={styles.note} title={messageText ? messageText : message}>{message}</div>}
        </div>
    </div>;
}, areEqual);

export default Card;
